import { createFunctionLibrary } from '@wix/thunderbolt-becky-root'
import type { BeckyModel, FeaturesModelInstance, SiteAssetsModule } from '@wix/thunderbolt-becky-root'
import { resolveBeckyModel } from './helpers/resolveBeckyModel'
import { maybeAppendErrorPages } from '../errorPages/getPayloadWithErrorPage'
import byRefModule from '../byRef'
import { BeckyFetch } from './utils/beckyFetch'
import { getBeckyTopology } from './helpers/topologyBuilder'
import { fetchComponentShapes } from '@wix/thunderbolt-fetch-shapes'
import type { RendererType } from '@wix/thunderbolt-symbols'
import { typedModelFactory } from './helpers/typedModelFactory'
import schemas from '@wix/document-services-json-schemas/dist/refData.json'

const featuresCarmiLoaders: Record<RendererType | 'viewer', FeaturesModelInstance> = {
	viewer: require('!carmi/loader?compiler=simple!../carmi/features.carmi'),
	react: require('!carmi/loader?compiler=simple!../carmi/features.react.carmi'),
	'react-native': require('!carmi/loader?compiler=simple!../carmi/features.reactNative.carmi'),
}

export const featuresModelFactory = (rendererType: RendererType | 'viewer') => {
	const features = featuresCarmiLoaders[rendererType]
	return typedModelFactory<FeaturesModelInstance>(features)
}

const featuresModule: SiteAssetsModule<FeaturesModelInstance> = (moduleContext) => {
	const { httpClient, metricsReporter, topologyGetter, logger } = moduleContext
	const fetchFn = BeckyFetch(httpClient.fetch, metricsReporter, logger)
	const funcLib = createFunctionLibrary({
		fetch: fetchFn,
		metricsReporter,
		schemas,
	})
	const byRef = byRefModule(moduleContext)

	return {
		execute: async (payload) => {
			const beckyTopology = getBeckyTopology(topologyGetter, payload)

			return byRef
				.execute(maybeAppendErrorPages(payload))
				.then((payloadWithResolvedRefs) =>
					resolveBeckyModel({
						payload: payloadWithResolvedRefs,
						beckyTopology,
						fetchFn,
						metricsReporter,
					})
				)
				.then(async (beckyModel) => {
					const shapeStore = await funcLib.runAsyncAndReport(
						async () =>
							fetchComponentShapes(
								beckyModel,
								fetchFn,
								beckyTopology.environmentTopology.mediaRootUrl,
								funcLib.buildSvgUrl,
								metricsReporter,
								schemas
							),
						'tb-features-fetch-shapes'
					)
					const beckyModelWithShapes: BeckyModel = { ...beckyModel, shapeStore }

					const featuresFuncLib = {
						...funcLib,
						setFetchRequestsStore: () => metricsReporter.reportError,
					}
					return funcLib.runAndReport<FeaturesModelInstance>(() => {
						const beckyModelFactory = featuresModelFactory(payload.rendererType || 'viewer')
						return beckyModelFactory(beckyModelWithShapes, featuresFuncLib)
					}, 'tb-features-model-calculation')
				})
				.then(({ stateRefs, structure, props }) => ({ stateRefs, structure, props }))
		},
	}
}

export default featuresModule
